import { FullPageCard, NavigationPage } from '@/app/components'
import { Button, Text } from '@/components'
import {
  CarFrontIcon,
  ClockIcon,
  DatabaseIcon,
  DollarSignIcon,
  HomeIcon,
} from 'lucide-react'
import { match } from 'ts-pattern'
import ThemedDocumentRenderer from '@/components/ThemedDocumentRenderer/ThemedDocumentRenderer'
import useViewModel from './useViewModel'
import ExpandableContent from '@/components/ExpandableContent/ExpandableContent'
import {
  MultiProgramDeviceFlowProvider,
  MultiProgramOnboardingFlowProvider,
} from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { useEffect } from 'react'
import ProgramRewardsListCard from '@/components/ProgramRewards/program-rewards-list-card'
import EnrollAdditionalField from '@/app/pages/utility-program/program-details/enroll-additional-field'
import { AlertMessage } from 'app-components'

const SUMMARY_HEIGHT = 100

export function ProgramDetailsOnboardingPage() {
  return (
    <MultiProgramOnboardingFlowProvider>
      <ProgramDetails />
    </MultiProgramOnboardingFlowProvider>
  )
}

export function ProgramDetailsDevicesPage() {
  return (
    <MultiProgramDeviceFlowProvider>
      <ProgramDetails />
    </MultiProgramDeviceFlowProvider>
  )
}

export default function ProgramDetails() {
  const {
    programContent,
    onBack,
    isEnrolled,
    onUnenrollClick,
    closeBottomSheet,
    rewardsByCategory,
    displayEnrollAdditional,
    utilityProgramId,
    onSuccessfulQuickEnroll,
    displayEnrollSuccess,
  } = useViewModel()

  useEffect(() => {
    return () => {
      closeBottomSheet()
    }
  }, [])

  if (!programContent) {
    return null
  }

  return (
    <FullPageCard>
      <NavigationPage
        id={'program-details-view-nav'}
        title={programContent.name}
        subtitle={programContent.subtitle}
        className="!p-0"
        backOverride={onBack}
      >
        <div className="flex flex-col gap-4 h-full">
          {displayEnrollSuccess && (
            <AlertMessage variant="success">
              Your additional device(s) have been successfully enrolled!
            </AlertMessage>
          )}
          {displayEnrollAdditional && (
            <EnrollAdditionalField
              programId={utilityProgramId}
              onSuccess={onSuccessfulQuickEnroll}
            />
          )}
          <ProgramRewardsListCard rewardsByCategory={rewardsByCategory} />
          <section className="flex flex-col gap-4 border border-solid border-themed-grey-100 w-full rounded-md">
            <div className="flex flex-col gap-4 p-4">
              <div className="flex flex-col gap-2">
                <Text variant="h3">Program Summary</Text>
                <ExpandableContent contentHeight={SUMMARY_HEIGHT}>
                  <ThemedDocumentRenderer document={programContent.summary} />
                </ExpandableContent>
              </div>
              {programContent?.logoUrl && (
                <div className={'flex flex-col gap-2'}>
                  <Text variant="h3-subheader">Sponsor</Text>
                  <img
                    src={programContent.logoUrl}
                    alt="sponsor logo"
                    className={'max-h-20 max-w-20'}
                  />
                </div>
              )}
            </div>
          </section>
          {programContent.giveValueChips.length > 0 && (
            <section className="flex flex-col p-4 gap-4 border border-solid border-themed-grey-100 rounded-md">
              <Text variant="h3">Give</Text>
              <div className="flex flex-row flex-wrap gap-2">
                {programContent.giveValueChips.map((c, index) => (
                  <ValueChip
                    key={index}
                    kind={c.kind as ValueChipKind}
                    title={c.text}
                  />
                ))}
              </div>
            </section>
          )}
          {programContent.getValueChips.length > 0 && (
            <section className="flex flex-col p-4 gap-4 border border-solid border-themed-grey-100 rounded-md">
              <Text variant="h3">Get</Text>
              <div className="flex flex-row flex-wrap gap-2">
                {programContent.getValueChips.map((c, index) => (
                  <ValueChip
                    key={index}
                    kind={c.kind as ValueChipKind}
                    title={c.text}
                  />
                ))}
              </div>
            </section>
          )}
          <div className="flex-auto" />
          {isEnrolled && (
            <Button
              id="program-details-unenroll-button"
              variant="secondary"
              onClick={() => onUnenrollClick()}
            >
              Unenroll
            </Button>
          )}
        </div>
      </NavigationPage>
    </FullPageCard>
  )
}
type ValueChipKind = 'time' | 'vehicle-permission' | 'info' | 'cash' | 'credits'
type ValueChipProps = {
  title: string
  kind: ValueChipKind
}

function ValueChip({ title, kind }: ValueChipProps) {
  const config = match(kind)
    .with('cash', () => ({
      icon: <DollarSignIcon size={18} />,
      classNames: 'bg-themed-green-900',
    }))
    .with('credits', () => ({
      icon: <DatabaseIcon size={18} />,
      classNames: 'bg-themed-blue-500',
    }))
    .with('time', () => ({
      icon: <ClockIcon size={18} />,
      classNames: 'bg-themed-blue-500',
    }))
    .with('vehicle-permission', () => ({
      icon: <CarFrontIcon size={18} />,
      classNames: 'bg-themed-accent',
    }))
    .with('info', () => ({
      icon: <HomeIcon size={18} />,
      classNames: 'bg-themed-green-900',
    }))
    .exhaustive()

  return (
    <>
      <div
        className={`flex flex-row gap-1.5 px-3 py-2 rounded-full text-white items-center ${config.classNames}`}
      >
        {config.icon}
        <Text variant={'body1'} className="text-white text-sm">
          {title}
        </Text>
      </div>
    </>
  )
}
